import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase, readableError } from '../lib/supabase'
import { useSession } from '../lib/session'
import { toSentenceCase } from '../lib/format'
import { getCached, setCached } from '../lib/queryCache'
import { Shell } from '../components/Shell'
import { Field, Sill, Spinner, initials } from '../components/ui'
import InvitePanel from '../components/InvitePanel'

/**
 * The people, not the songs. Two halves that answer different questions:
 * circles you keep (who hears your work early) and circles you're in (whose
 * work reaches you). Songs shared into a circle surface on the Collective tab
 * alongside everything else within reach — splitting them across two screens
 * would mean checking two places for the same thing.
 */
export default function Circles() {
  const { user } = useSession()
  const nav = useNavigate()

  const [mine, setMine] = useState(() => getCached('circles-mine:' + user.id))
  const [inOthers, setInOthers] = useState(() => getCached('circles-in:' + user.id) ?? [])
  const [open, setOpen] = useState(null)     // circle id whose members are showing
  const [naming, setNaming] = useState(null) // '' while the new-circle field is up
  const [adding, setAdding] = useState('')   // email being added to `open`
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [codeFor, setCodeFor] = useState(null) // circle id whose invite panel is showing
  const [codes, setCodes] = useState({})       // circle id -> invite_codes row, cached per open

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('circles')
      .select('*, circle_members(user_id, added_at, users(id, first_name, last_name, avatar_url))')
      .order('kind')
      .order('created_at')
    if (error) return setErr(readableError(error))
    const mineList = (data ?? []).filter((c) => c.owner_id === user.id)
    const inList = (data ?? []).filter((c) => c.owner_id !== user.id)
    setCached('circles-mine:' + user.id, mineList)
    setCached('circles-in:' + user.id, inList)
    setMine(mineList)
    setInOthers(inList)
  }, [user.id])

  useEffect(() => { load() }, [load])

  if (mine === null) return <Shell tabs><Spinner /></Shell>

  const create = async () => {
    const name = toSentenceCase(naming)
    if (!name) return
    setBusy(true); setErr(null)
    const { error } = await supabase.from('circles')
      .insert({ owner_id: user.id, name, kind: 'writers' })
    setBusy(false)
    if (error) return setErr(readableError(error))
    setNaming(null)
    load()
  }

  const addPerson = async (circle) => {
    const email = adding.trim().toLowerCase()
    if (!email) return
    setBusy(true); setErr(null)

    // A circle is people, so it needs a real account to point at. RLS keeps
    // this lookup honest: it only resolves someone already visible to you.
    const { data: found } = await supabase.from('users').select('id').eq('email', email).maybeSingle()
    if (!found) {
      setBusy(false)
      return setErr(`Nobody on Beseter uses ${email} yet. They'll need an account first.`)
    }
    const { error } = await supabase.from('circle_members')
      .insert({ circle_id: circle.id, user_id: found.id })
    setBusy(false)
    if (error) return setErr(readableError(error))
    setAdding('')
    load()
  }

  const shareCode = async (circle) => {
    if (codeFor === circle.id) return setCodeFor(null)
    setErr(null)
    if (!codes[circle.id]) {
      const { data, error } = await supabase.rpc('create_invite_code',
        { p_kind: 'circle', p_circle_id: circle.id })
      if (error) return setErr(readableError(error))
      setCodes((prev) => ({ ...prev, [circle.id]: data }))
    }
    setCodeFor(circle.id)
  }

  const removePerson = async (circle, uid) => {
    const { error } = await supabase.from('circle_members')
      .delete().eq('circle_id', circle.id).eq('user_id', uid)
    if (error) return setErr(readableError(error))
    load()
  }

  const dropCircle = async (circle) => {
    const { error } = await supabase.from('circles').delete().eq('id', circle.id)
    if (error) return setErr(readableError(error))
    setOpen(null)
    load()
  }

  const leave = async (circle) => {
    const { error } = await supabase.from('circle_members')
      .delete().eq('circle_id', circle.id).eq('user_id', user.id)
    if (error) return setErr(readableError(error))
    load()
  }

  const card = (c) => {
    const members = c.circle_members ?? []
    const isOpen = open === c.id
    const isInner = c.kind === 'inner'
    return (
      <div key={c.id} className={`tile tile-flush circle${isOpen ? ' open' : ''}`}>
        <button className="circle-head" onClick={() => { setOpen(isOpen ? null : c.id); setAdding(''); setErr(null) }}
                aria-expanded={isOpen}>
          <span className={`circle-glyph${isInner ? ' inner' : ''}`} aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="1.8">
              <circle cx="12" cy="12" r="9.2" />
              {isInner && <circle cx="12" cy="12" r="3.6" fill="currentColor" stroke="none" />}
            </svg>
          </span>
          <span className="circle-name">
            {c.name}
            <small>
              {isInner ? 'Your one small room · ' : ''}
              {members.length === 0 ? 'nobody yet'
                : `${members.length} ${members.length === 1 ? 'person' : 'people'}`}
            </small>
          </span>
          <span className="circle-caret" aria-hidden="true">{isOpen ? '−' : '+'}</span>
        </button>

        {isOpen && (
          <div className="circle-body">
            {members.length === 0 && (
              <p className="small">
                {isInner
                  ? 'The people you would play a half-finished song to. Keep it small — that is the whole point of it.'
                  : 'Add the writers you share a room with.'}
              </p>
            )}
            {members.map((m) => (
              <div className="split" key={m.user_id}>
                <span className="ava">
                  {initials([m.users?.first_name, m.users?.last_name].filter(Boolean).join(' '))}
                </span>
                <span className="nm">
                  {[m.users?.first_name, m.users?.last_name].filter(Boolean).join(' ') || 'A writer'}
                  <small>In since {new Date(m.added_at).toLocaleDateString()}</small>
                </span>
                <button className="btn-plain" style={{ minHeight: 0, padding: '0 4px' }}
                        onClick={() => removePerson(c, m.user_id)}
                        aria-label="Remove from this circle">×</button>
              </div>
            ))}

            <div style={{ marginTop: 14 }}>
              <Field label="Add someone by email">
                <input className="input" type="email" value={adding} placeholder="them@example.com"
                       onChange={(e) => setAdding(e.target.value)}
                       onKeyDown={(e) => { if (e.key === 'Enter') addPerson(c) }} />
              </Field>
              <button className="btn btn-primary" disabled={busy || !adding.includes('@')}
                      onClick={() => addPerson(c)}>
                Add them
              </button>
            </div>

            <div style={{ marginTop: 10 }}>
              <button className="btn-plain" style={{ paddingLeft: 0 }} onClick={() => shareCode(c)}>
                {codeFor === c.id ? 'Hide the code' : "No account yet? Invite by code"}
              </button>
              {codeFor === c.id && codes[c.id] && (
                <InvitePanel code={codes[c.id].code} kind="circle" label={c.name} />
              )}
            </div>

            {!isInner && (
              <button className="btn-plain" style={{ marginTop: 10, color: 'var(--danger)' }}
                      onClick={() => dropCircle(c)}>
                Delete this circle
              </button>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <Shell tabs>
      <div className="eyebrow">Circles</div>
      <h2 className="q">The people you write to.</h2>
      <p className="sub">
        A circle is a room. Put a song in one and only the people standing in it
        hear it — no church, no queue, no decision at the end.
      </p>
      <Sill />

      {mine.map(card)}

      {naming === null ? (
        <button className="btn btn-quiet" onClick={() => setNaming('')}>+ New circle</button>
      ) : (
        <div className="tile newcircle">
          <Field label="What do you call it?">
            <input className="input" autoFocus value={naming} placeholder="Tuesday Co-write"
                   onChange={(e) => setNaming(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter') create() }} />
          </Field>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" disabled={busy || !naming.trim()} onClick={create}>
              Make it
            </button>
            <button className="btn btn-quiet" onClick={() => setNaming(null)}>Cancel</button>
          </div>
        </div>
      )}

      {inOthers.length > 0 && (
        <>
          <div className="eyebrow" style={{ marginTop: 28 }}>Circles you're in</div>
          {inOthers.map((c) => (
            <div key={c.id} className="tile tile-flush">
              <div className="circle-head static">
                <span className="circle-glyph" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       strokeWidth="1.8"><circle cx="12" cy="12" r="9.2" /></svg>
                </span>
                <span className="circle-name">
                  {c.name}
                  <small>{c.circle_members?.length ?? 0} people · their songs reach you</small>
                </span>
                <button className="btn-plain" style={{ minHeight: 0, fontSize: 12.5 }}
                        onClick={() => leave(c)}>Leave</button>
              </div>
            </div>
          ))}
        </>
      )}

      {err && <p className="err">{err}</p>}

      <div className="hr" />
      <button className="action col" onClick={() => nav('/me')}>
        <div className="row"><span>Put a song in a circle</span><span className="ar">›</span></div>
        <small>Open a finished song and choose how far it goes</small>
      </button>
    </Shell>
  )
}
